// @ts-ignore esModuleInterop
import React, { useMemo } from 'react'
import _ from 'lodash'
import _cx from 'classnames/bind'
// @ts-ignore no types
import numeral from 'numeral'
import { Grid, Icon, Text } from '@adc/ui-adc'
import * as graph from '@adc/sdk'
import type { AuctionBlueprintOnlineReactiveArgs } from '@adc/blueprint'
import { CombinedListingType, useListingContext } from 'common/context/listing'
import {
  formatNumber,
  formatMoneyWithCentsExceptZero,
} from '../../../utils/formatters'
import { getMarketingAttribute } from '../../../utils/graph'
// @ts-ignore no types
import { getListPrice } from '../../../utils/listing'
import cls from './index.scss'

const cx = _cx.bind(cls)
const elmId = 'valuation_details'

/**
 * This value seems to have been copy/pasted in lots of places in this app. I have extracted here so we can do exact
 * matches without copy/pasting strings throughout the file.
 *
 * TODO: Extract this into constant and replace hard coded instances of this string that are present for the same
 * purpose.
 */
const NULL_VALUE = 'TBD'

numeral.defaultFormat('$0,0')
numeral.nullFormat(NULL_VALUE)

interface getMarketingAttributeValueParams {
  attributeType: string
  openingBidFormatter: any
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
  value: any
}

const getMarketingAttributeValue = ({
  attributeType,
  // eslint-disable-next-line @typescript-eslint/unbound-method
  openingBidFormatter = _.identity,
  listing,
  value,
}: getMarketingAttributeValueParams) => {
  const attr = getMarketingAttribute(listing, attributeType)
  if (!attr.show || !(value || attr.default_display_value !== 'HIDDEN'))
    return NULL_VALUE
  return (
    (value
      ? openingBidFormatter(value, listing)
      : attr.default_display_value) || NULL_VALUE
  )
}

interface ValuationDetailsProps {
  buyerViews?: number
  featureFlags?: any
  isPropertyReportAvailable?: boolean
  realtyTrackReport?: any
  showRentalEstimate?: boolean
}

const openingBidFormatter = (val: number, listing: CombinedListingType) => {
  return listing.online_auction_segment &&
    listing.listing_configuration?.asset_type ===
      graph.enums.AssetType.Foreclosure
    ? formatMoneyWithCentsExceptZero(val, NULL_VALUE)
    : numeral(val).format('$0,0', Math.floor)
}

const SeeComparableSales = ({
  isPropertyReportAvailable,
}: {
  isPropertyReportAvailable: boolean
}) => {
  const { listing } = useListingContext()
  const arv = listing?.primary_property?.summary?.valuation

  return listing.__methods__.isSparkOffer() ? null : (
    <Text className={cx('comp-sales')} size='mini'>
      {isPropertyReportAvailable && arv && !isNaN(arv) && (
        <a href='#comp_sales'>See Comparable Sales</a>
      )}
    </Text>
  )
}

const ValuationDetails = ({
  buyerViews,
  isPropertyReportAvailable,
}: ValuationDetailsProps) => {
  const { listing } = useListingContext()
  const showListPrice = listing.__methods__.shouldDisplayListPrice()
  const shouldShowOpeningBidRow = () => {
    return listing.__methods__.showOpeningBid() && !showListPrice
  }
  const showOpeningBidRow = shouldShowOpeningBidRow()
  const bedBaths = useMemo(
    () =>
      [
        [listing.primary_property?.summary?.total_bedrooms, 'Beds'],
        [listing.primary_property?.summary?.total_bathrooms, 'Baths'],
        [
          formatNumber(listing.primary_property?.summary?.living_size),
          'Sq. Feet',
        ],
      ].map(([val, unit]) => (
        <div key={unit} className={cx('beds-and-baths-item')}>
          <Text.Header
            as='h2'
            level={1}
            compact
            // @ts-ignore need to export type from component in DS, keyof typeof SIZES_MAP
            size='massive'
            weight='heavy'
            className={cx('beds-and-baths-amount')}
          >
            {val || ' - '}
          </Text.Header>
          <Text size='tiny' color='gray'>
            {unit}
          </Text>
        </div>
      )),
    [
      listing.primary_property?.summary?.total_bedrooms,
      listing.primary_property?.summary?.total_bathrooms,
      listing.primary_property?.summary?.living_size,
    ]
  )

  return (
    <div className={cx('investment-info')}>
      <Grid>
        <Grid.Row>
          <Grid.Column computer={8} tablet={8} mobile={16}>
            <div className='u-mb-2'>
              {showListPrice || showOpeningBidRow ? (
                <Text size='tiny' color='gray'>
                  {showListPrice && 'List Price'}
                  {showOpeningBidRow && 'Opening Bid'}
                </Text>
              ) : null}
              <div className={cx('price-and-sales')}>
                <Text.Header
                  as='h2'
                  level={1}
                  compact
                  // @ts-ignore need to export type from component in DS, keyof typeof SIZES_MAP
                  size='massive'
                  weight='heavy'
                >
                  {showListPrice && getListPrice(listing)}
                  {showOpeningBidRow &&
                    getMarketingAttributeValue({
                      attributeType: 'OPENING_BID',
                      openingBidFormatter,
                      listing,
                      value: _.get(listing, 'auction.starting_bid', 0),
                    })}
                </Text.Header>
                <SeeComparableSales
                  isPropertyReportAvailable={!!isPropertyReportAvailable}
                />
              </div>
            </div>
            <Text.Header
              as='h1'
              level={3}
              color='gray'
              // @ts-ignore need to export type from component in DS, keyof typeof WEIGHTS_MAP
              weight='normal'
              compact
              data-elm-id={`${elmId}_address`}
            >
              {listing.formatted_address?.[0]}
              <div className={cx('spacer')} />
              <Text.Header
                as='h1'
                level={3}
                color='gray'
                // @ts-ignore need to export type from component in DS, keyof typeof WEIGHTS_MAP
                weight='normal'
                compact
                data-elm-id={`${elmId}_location`}
              >
                {listing.formatted_address?.[1]}
              </Text.Header>
            </Text.Header>
          </Grid.Column>
          <Grid.Column computer={8} tablet={8} mobile={16} stretched>
            <div className={cx('attributes-and-views')}>
              {bedBaths && (
                <div className={cx('beds-and-baths')}>{bedBaths}</div>
              )}
              {buyerViews && buyerViews >= 10 && (
                <Text
                  data-elm-id={`${elmId}_views`}
                  className={cx('u-nowrap', 'property-views')}
                  size='small'
                  color='gray'
                >
                  <Icon name='user outline' />
                  {formatNumber(buyerViews)} Views
                </Text>
              )}
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  )
}

export default ValuationDetails
